import { Component, ComponentType } from "@/types";
import { BaseCommandHandler, CommandResult, PropertyChange } from "../BaseCommandHandler";
import { createThemePatterns, createColorPatterns } from "./ControlPatternMixins";

const VALID_THEMES = ['circular', 'rounded-rect', 'pill', 'ghost', 'neon', 'material'] as const;

const THEME_ALIASES: Record<string, string> = {
  'round': 'circular',
  'rect': 'rounded-rect',
  'outline': 'ghost',
  'transparent': 'ghost',
};

export class IconButtonCommandHandler extends BaseCommandHandler {
  constructor() {
    super();
    this.initializePatterns();
  }

  private initializePatterns(): void {
    // Theme patterns
    for (const [regex, handler] of createThemePatterns([...VALID_THEMES], THEME_ALIASES)) {
      this.commandPatterns.set(regex, handler);
    }

    // Color patterns
    for (const [regex, handler] of createColorPatterns(['activeColor', 'inactiveColor', 'activeIconColor', 'inactiveIconColor', 'borderColor', 'hoverColor'])) {
      this.commandPatterns.set(regex, handler);
    }

    // Icon name
    this.commandPatterns.set(
      /(?:set )?icon (?:to )?(\w[\w-]*)/i,
      (match) => ({ properties: { iconName: match[1].trim() } })
    );

    // Shape
    this.commandPatterns.set(
      /(?:set )?shape (?:to )?(rounded[- ]?rect|circle|circular|round|pill|square)/i,
      (match) => {
        const raw = match[1].toLowerCase().replace(/\s+/g, '-');
        const shapeMap: Record<string, string> = {
          circle: 'circular', round: 'circular', circular: 'circular',
          pill: 'pill', square: 'square',
          'rounded-rect': 'rounded-rect', roundedrect: 'rounded-rect',
        };
        return { properties: { shape: shapeMap[raw] || 'circular' } };
      }
    );

    // Icon size
    this.commandPatterns.set(
      /(?:set )?(?:icon )?size (?:to |= ?)?(\d+)/i,
      (match) => ({ properties: { iconSize: parseInt(match[1]) } })
    );

    // Show/hide label
    this.commandPatterns.set(
      /show label/i,
      () => ({ properties: { showLabel: true } })
    );

    this.commandPatterns.set(
      /hide label/i,
      () => ({ properties: { showLabel: false } })
    );

    // Label text
    this.commandPatterns.set(
      /(?:set )?label (?:to )?["']?(.+?)["']?\s*$/i,
      (match) => ({ properties: { label: match[1].trim(), showLabel: true } })
    );

    // Active/inactive toggle
    this.commandPatterns.set(
      /(?:make |set )?(active|inactive)/i,
      (match) => ({ properties: { isActive: match[1].toLowerCase() === 'active' } })
    );
  }

  canHandle(command: string, component: Component): boolean {
    return component.type === ComponentType.ICON_BUTTON && this.hasKeywords(command);
  }

  private hasKeywords(command: string): boolean {
    const keywords = [
      "icon button", "icon", "active", "inactive", "shape", "theme",
      "label", "show", "hide", "size", "color",
      "circular", "pill", "ghost", "neon", "material", "rounded",
    ];
    const lowerCommand = command.toLowerCase();
    return keywords.some(keyword => lowerCommand.includes(keyword));
  }

  async parseCommand(command: string, component: Component): Promise<CommandResult> {
    const commandParts = command.split(/\s+(?:and|with|also)\s+/i);
    const changes: PropertyChange = { properties: {} };

    for (const part of commandParts) {
      const trimmed = part.trim();
      if (!trimmed) continue;
      const patternMatch = this.matchPatterns(trimmed, component);
      if (patternMatch?.properties) {
        changes.properties = { ...changes.properties, ...patternMatch.properties };
      }
      if (patternMatch?.style) {
        changes.style = { ...changes.style, ...patternMatch.style };
      }
    }

    if (!changes.properties || Object.keys(changes.properties).length === 0) {
      return {
        success: false,
        error: "Could not understand icon button command",
        suggestions: await this.getExampleCommands(ComponentType.ICON_BUTTON),
      };
    }

    const validation = this.validateChanges(changes, component);
    if (!validation.valid) {
      return { success: false, error: validation.error };
    }

    return {
      success: true,
      command: {
        type: this.determineCommandType(changes),
        changes,
        confidence: this.calculateConfidence(command, changes),
        description: this.generateDescription(changes, component),
      },
    };
  }

  async getExampleCommands(_componentType: ComponentType): Promise<string[]> {
    return [
      "Set icon to Lightbulb",
      "Theme ghost",
      "Active color blue",
      "Shape circular",
      "Show label",
      "Make active",
      "Set label to Power",
      "Size 32",
    ];
  }

  async getSuggestions(component: Component): Promise<string[]> {
    const suggestions: string[] = [];
    const props = (component.properties || {}) as Record<string, unknown>;
    const currentTheme = (props.theme as string) ?? 'circular';
    const isActive = (props.isActive as boolean) ?? false;

    suggestions.push(isActive ? "Make inactive" : "Make active");

    const otherThemes = VALID_THEMES.filter(t => t !== currentTheme);
    if (otherThemes.length > 0) {
      suggestions.push(`Theme ${otherThemes[0]}`);
    }

    suggestions.push("Set icon to Lightbulb");
    suggestions.push("Active color blue");
    suggestions.push("Shape circular");
    suggestions.push("Show label");

    return suggestions.slice(0, 6);
  }
}

export const iconButtonCommandHandler = new IconButtonCommandHandler();
